import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useUser } from '../../../contexts/UserContext';
import { useTheme } from '../../../contexts/ThemeContext';
import type { Order, Pickup, Callback, User, Call, Chat, Pagination, TransferContact } from '../types';

const PAGE_SIZE = 25;

interface AdminData {
  orders: Order[];
  returns: Order[];
  pickups: Pickup[];
  callbacks: Callback[];
  users: User[];
  calls: Call[];
  textChats: Chat[];
  webVoiceChats: Chat[];
  transferContacts: Record<string, TransferContact>;
  ordersPagination: Pagination | null;
  returnsPagination: Pagination | null;
  pickupsPagination: Pagination | null;
  callbacksPagination: Pagination | null;
  callsPagination: Pagination | null;
  textChatsPagination: Pagination | null;
  voiceChatsPagination: Pagination | null;
}

interface AdminContextValue {
  // Auth state
  isAdmin: boolean;
  loading: boolean;
  currentUserEmail: string | null;
  getAuthHeaders: () => Promise<Record<string, string>>;

  // Data
  data: AdminData;
  ordersPage: number;
  returnsPage: number;
  pickupsPage: number;
  callbacksPage: number;
  callsPage: number;
  textChatsPage: number;
  voiceChatsPage: number;

  // Actions
  loadData: () => Promise<void>;
  changeOrdersPage: (page: number) => Promise<void>;
  changeReturnsPage: (page: number) => Promise<void>;
  changePickupsPage: (page: number) => Promise<void>;
  changeCallbacksPage: (page: number) => Promise<void>;
  changeCallsPage: (page: number) => Promise<void>;
  changeTextChatsPage: (page: number) => Promise<void>;
  changeVoiceChatsPage: (page: number) => Promise<void>;
  updateUsers: (updater: (users: User[]) => User[]) => void;
  loadTransferDirectory: () => Promise<void>;
  saveTransferDirectory: (contacts: Record<string, TransferContact>) => Promise<boolean>;

  // Helpers
  maskEmail: (email: string) => string;
  maskPhone: (phone: string | undefined) => string;
  isOwnData: (email: string) => boolean;
  formatDate: (timestamp?: number) => string;
  formatPrice: (price: number) => string;
}

const AdminContext = createContext<AdminContextValue | null>(null);

export function AdminProvider({ children }: { children: ReactNode }) {
  const { user, getAuthHeaders } = useUser();
  const { actAsUser } = useTheme();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Build headers with optional X-Act-As-User for impersonation
  const getHeaders = useCallback(async () => {
    const headers = await getAuthHeaders();
    if (actAsUser?.email) {
      headers['X-Act-As-User'] = actAsUser.email;
    }
    return headers;
  }, [getAuthHeaders, actAsUser]);
  const [ordersPage, setOrdersPage] = useState(1);
  const [returnsPage, setReturnsPage] = useState(1);
  const [pickupsPage, setPickupsPage] = useState(1);
  const [callbacksPage, setCallbacksPage] = useState(1);
  const [callsPage, setCallsPage] = useState(1);
  const [textChatsPage, setTextChatsPage] = useState(1);
  const [voiceChatsPage, setVoiceChatsPage] = useState(1);

  const [data, setData] = useState<AdminData>({
    orders: [],
    returns: [],
    pickups: [],
    callbacks: [],
    users: [],
    calls: [],
    textChats: [],
    webVoiceChats: [],
    transferContacts: {},
    ordersPagination: null,
    returnsPagination: null,
    pickupsPagination: null,
    callbacksPagination: null,
    callsPagination: null,
    textChatsPagination: null,
    voiceChatsPagination: null,
  });

  const loadData = useCallback(async () => {
    const headers = await getHeaders();

    const [ordersRes, returnsRes, pickupsRes, callbacksRes, usersRes, callsRes, textChatsRes, voiceChatsRes] = await Promise.all([
      fetch(`/api/admin/orders?page=1&page_size=${PAGE_SIZE}`, { headers }),
      fetch(`/api/admin/returns?page=1&page_size=${PAGE_SIZE}`, { headers }),
      fetch(`/api/admin/pickups?page=1&page_size=${PAGE_SIZE}`, { headers }),
      fetch(`/api/admin/callbacks?page=1&page_size=${PAGE_SIZE}`, { headers }),
      fetch('/api/users', { headers }),
      fetch(`/api/admin/calls?page=1&page_size=${PAGE_SIZE}`, { headers }),
      fetch(`/api/admin/chats?page=1&page_size=${PAGE_SIZE}&channel=text`, { headers }),
      fetch(`/api/admin/chats?page=1&page_size=${PAGE_SIZE}&channel=web_voice`, { headers }),
    ]);

    const [ordersData, returnsData, pickupsData, callbacksData, usersData, callsData, textChatsData, voiceChatsData] = await Promise.all([
      ordersRes.json(),
      returnsRes.json(),
      pickupsRes.json(),
      callbacksRes.json(),
      usersRes.json(),
      callsRes.json(),
      textChatsRes.json(),
      voiceChatsRes.json(),
    ]);

    setOrdersPage(1);
    setReturnsPage(1);
    setPickupsPage(1);
    setCallbacksPage(1);
    setCallsPage(1);
    setTextChatsPage(1);
    setVoiceChatsPage(1);
    setData(prev => ({
      ...prev,
      orders: ordersData.orders || [],
      returns: returnsData.returns || [],
      pickups: pickupsData.pickups || [],
      callbacks: callbacksData.callbacks || [],
      users: usersData.users || [],
      calls: callsData.calls || [],
      textChats: textChatsData.chats || [],
      webVoiceChats: voiceChatsData.chats || [],
      ordersPagination: ordersData.pagination || null,
      returnsPagination: returnsData.pagination || null,
      pickupsPagination: pickupsData.pagination || null,
      callbacksPagination: callbacksData.pagination || null,
      callsPagination: callsData.pagination || null,
      textChatsPagination: textChatsData.pagination || null,
      voiceChatsPagination: voiceChatsData.pagination || null,
    }));
  }, [getHeaders]);

  // Initialize admin status and load data
  useEffect(() => {
    const init = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const headers = await getHeaders();
        const response = await fetch('/api/admin/status', { headers });
        const statusData = await response.json();
        setIsAdmin(statusData.is_admin || false);
        await loadData();
      } catch {
        setIsAdmin(false);
      }
      setLoading(false);
    };
    init();
  }, [user, getHeaders, loadData]);

  // Reload data and update admin status when actAsUser changes (impersonation toggle)
  useEffect(() => {
    if (!user || loading) return;

    const refresh = async () => {
      const headers = await getHeaders();
      const response = await fetch('/api/admin/status', { headers });
      const statusData = await response.json();
      setIsAdmin(statusData.is_admin || false);
      await loadData();
    };
    refresh();
  }, [actAsUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeOrdersPage = useCallback(async (newPage: number) => {
    setOrdersPage(newPage);
    const headers = await getHeaders();
    const res = await fetch(`/api/admin/orders?page=${newPage}&page_size=${PAGE_SIZE}`, { headers });
    const ordersData = await res.json();
    setData(prev => ({
      ...prev,
      orders: ordersData.orders || [],
      ordersPagination: ordersData.pagination || null,
    }));
  }, [getHeaders]);

  const changeReturnsPage = useCallback(async (newPage: number) => {
    setReturnsPage(newPage);
    const headers = await getHeaders();
    const res = await fetch(`/api/admin/returns?page=${newPage}&page_size=${PAGE_SIZE}`, { headers });
    const returnsData = await res.json();
    setData(prev => ({
      ...prev,
      returns: returnsData.returns || [],
      returnsPagination: returnsData.pagination || null,
    }));
  }, [getHeaders]);

  const changePickupsPage = useCallback(async (newPage: number) => {
    setPickupsPage(newPage);
    const headers = await getHeaders();
    const res = await fetch(`/api/admin/pickups?page=${newPage}&page_size=${PAGE_SIZE}`, { headers });
    const pickupsData = await res.json();
    setData(prev => ({
      ...prev,
      pickups: pickupsData.pickups || [],
      pickupsPagination: pickupsData.pagination || null,
    }));
  }, [getHeaders]);

  const changeCallbacksPage = useCallback(async (newPage: number) => {
    setCallbacksPage(newPage);
    const headers = await getHeaders();
    const res = await fetch(`/api/admin/callbacks?page=${newPage}&page_size=${PAGE_SIZE}`, { headers });
    const callbacksData = await res.json();
    setData(prev => ({
      ...prev,
      callbacks: callbacksData.callbacks || [],
      callbacksPagination: callbacksData.pagination || null,
    }));
  }, [getHeaders]);

  const changeCallsPage = useCallback(async (newPage: number) => {
    setCallsPage(newPage);
    const headers = await getHeaders();
    const res = await fetch(`/api/admin/calls?page=${newPage}&page_size=${PAGE_SIZE}`, { headers });
    const callsData = await res.json();
    setData(prev => ({
      ...prev,
      calls: callsData.calls || [],
      callsPagination: callsData.pagination || null,
    }));
  }, [getHeaders]);

  const changeTextChatsPage = useCallback(async (newPage: number) => {
    setTextChatsPage(newPage);
    const headers = await getHeaders();
    const res = await fetch(`/api/admin/chats?page=${newPage}&page_size=${PAGE_SIZE}&channel=text`, { headers });
    const chatsData = await res.json();
    setData(prev => ({
      ...prev,
      textChats: chatsData.chats || [],
      textChatsPagination: chatsData.pagination || null,
    }));
  }, [getHeaders]);

  const changeVoiceChatsPage = useCallback(async (newPage: number) => {
    setVoiceChatsPage(newPage);
    const headers = await getHeaders();
    const res = await fetch(`/api/admin/chats?page=${newPage}&page_size=${PAGE_SIZE}&channel=web_voice`, { headers });
    const chatsData = await res.json();
    setData(prev => ({
      ...prev,
      webVoiceChats: chatsData.chats || [],
      voiceChatsPagination: chatsData.pagination || null,
    }));
  }, [getHeaders]);

  const updateUsers = useCallback((updater: (users: User[]) => User[]) => {
    setData(prev => ({
      ...prev,
      users: updater(prev.users),
    }));
  }, []);

  const loadTransferDirectory = useCallback(async () => {
    const headers = await getHeaders();
    const res = await fetch('/api/admin/transfer-directory', { headers });
    const result = await res.json();
    if (result.contacts) {
      setData(prev => ({ ...prev, transferContacts: result.contacts }));
    }
  }, [getHeaders]);

  const saveTransferDirectory = useCallback(async (contacts: Record<string, TransferContact>): Promise<boolean> => {
    const headers = await getHeaders();
    const res = await fetch('/api/admin/transfer-directory', {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ contacts }),
    });
    const result = await res.json();
    if (result.success) {
      setData(prev => ({ ...prev, transferContacts: contacts }));
      return true;
    }
    return false;
  }, [getHeaders]);

  // Helper functions
  const maskEmail = useCallback((email: string) => {
    if (isAdmin || email === user?.email) return email;
    const [local, domain] = email.split('@');
    if (!domain) return '***';
    return `${local.slice(0, 2)}***@${domain}`;
  }, [isAdmin, user?.email]);

  const maskPhone = useCallback((phone: string | undefined) => {
    if (!phone) return '-';
    if (isAdmin) return phone;
    return `***${phone.slice(-4)}`;
  }, [isAdmin]);

  const isOwnData = useCallback((email: string) => email === user?.email, [user?.email]);

  const formatDate = useCallback((timestamp?: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }, []);

  const formatPrice = useCallback((price: number) => {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(price);
  }, []);

  return (
    <AdminContext.Provider
      value={{
        isAdmin,
        loading,
        currentUserEmail: user?.email || null,
        getAuthHeaders,
        data,
        ordersPage,
        returnsPage,
        pickupsPage,
        callbacksPage,
        callsPage,
        textChatsPage,
        voiceChatsPage,
        loadData,
        changeOrdersPage,
        changeReturnsPage,
        changePickupsPage,
        changeCallbacksPage,
        changeCallsPage,
        changeTextChatsPage,
        changeVoiceChatsPage,
        updateUsers,
        loadTransferDirectory,
        saveTransferDirectory,
        maskEmail,
        maskPhone,
        isOwnData,
        formatDate,
        formatPrice,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdminContext() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdminContext must be used within AdminProvider');
  }
  return context;
}
